import { useEffect, useState } from "react";
import { InfinityMark } from "../components/ConnectMyHubLogo";
import { useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { toast, Toaster } from "sonner";
import { Loader2, CheckCircle2, MapPin, ShieldAlert, PauseCircle } from "lucide-react";

const SALES_SOURCES = [
  ["STAFFHUB_WEBSITE","ConnectMyHub Website"],["REFERRAL","Referral"],["PARTNER","Partner"],
  ["LEADHUB","LeadHub"],["EXTERNAL_AIA","External AIA"],["HUMAN_SALES","Human Sales"],
  ["BDR","BDR / SDR"],["SALES_PARTNER","Sales Partner"],["ORGANIC_SEARCH","Organic Search"],
  ["PAID_ADVERTISING","Paid Advertising"],["SOCIAL_MEDIA","Social Media"],["OUTBOUND","Outbound"],
  ["EVENT_NETWORKING","Event / Networking"],["EXISTING_MPS_CLIENT","Existing MPS Client"],["OTHER","Other"],
];

const STATUS_TONE = {
  ACTIVE:  "text-emerald-800 bg-emerald-50 border-emerald-200",
  BLOCKED: "text-red-700 bg-red-50 border-red-200",
  REVIEW:  "text-amber-800 bg-amber-50 border-amber-200",
};
const STATUS_ICON = { ACTIVE: CheckCircle2, BLOCKED: ShieldAlert, REVIEW: PauseCircle };

export default function EmployerSignup() {
  const nav = useNavigate();
  const [form, setForm] = useState({first_name:"",last_name:"",email:"",password:"",
    company_name:"",industry:"",country:"",website:"",phone:"",sales_source:"STAFFHUB_WEBSITE"});
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [verifyBusy, setVerifyBusy] = useState(false);
  const [token, setToken] = useState("");
  // Hiring states: live data from the existing State Availability & Compliance Engine.
  const [allStates, setAllStates] = useState([]);
  const [pickedStates, setPickedStates] = useState([]);
  const [stateFilter, setStateFilter] = useState("");

  useEffect(() => {
    api.get("/public/state-availability")
      .then(r => setAllStates(r.data.states || []))
      .catch(() => {});
  }, []);

  const toggleState = (s) => setPickedStates(cur =>
    cur.includes(s.state) ? cur.filter(x => x !== s.state) : [...cur, s.state]);

  const summary = pickedStates.reduce((acc, name) => {
    const row = allStates.find(x => x.state === name);
    if (row) acc[row.status] = (acc[row.status] || 0) + 1;
    return acc;
  }, {});

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.post("/employer-portal/signup",
        { ...form, hiring_states: pickedStates });
      setResult(data); setToken(data.verify_token || "");
      if (data.email_sent) toast.success("Check your inbox — a verification link has been sent");
      else toast.success("Signup received — use the verification token below (email not yet configured)");
    }
    catch (err) { toast.error(err.response?.data?.detail || "Signup failed"); }
    finally { setBusy(false); }
  };

  const verify = async () => {
    setVerifyBusy(true);
    try {
      await api.post("/employer-portal/verify-email", {email: result.email, token});
      toast.success("Email verified. A ConnectMyHub admin will review your account shortly.");
      setTimeout(()=>{ window.location.href = "/employer-portal/status"; }, 900);
    } catch (e) { toast.error(e.response?.data?.detail || "Verification failed"); }
    finally { setVerifyBusy(false); }
  };

  const filteredStates = allStates.filter(s =>
    !stateFilter || s.state.toLowerCase().includes(stateFilter.toLowerCase())
                 || s.state_code.toLowerCase().includes(stateFilter.toLowerCase()));

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-8">
      <div className="max-w-3xl w-full bg-white border border-slate-200 rounded-2xl shadow-lg p-8">
        <div className="flex items-center gap-3 mb-1">
          <div className="w-11 h-11 rounded-xl bg-slate-900 flex items-center justify-center"><InfinityMark size={30}/></div>
          <div><div className="text-[11px] font-semibold uppercase tracking-widest text-sky-600">Employer signup</div>
            <h1 className="font-display text-2xl font-bold text-slate-900">Join ConnectMyHub™</h1></div>
        </div>
        <p className="text-sm text-slate-500 mb-6">Create your company account. ConnectMyHub will verify your email, then an admin will review and approve you before Talent Network access is unlocked.</p>

        {!result ? (
          <form onSubmit={submit} className="grid md:grid-cols-2 gap-3">
            <Fld k="first_name" label="Your first name" f={form} setF={setForm} required/>
            <Fld k="last_name" label="Your last name" f={form} setF={setForm} required/>
            <Fld k="email" label="Work email" type="email" f={form} setF={setForm} required/>
            <Fld k="password" label="Password (10+ chars, letters + digit)" type="password" f={form} setF={setForm} required/>
            <Fld k="company_name" label="Company name" f={form} setF={setForm} required className="md:col-span-2"/>
            <Fld k="industry" label="Industry" f={form} setF={setForm}/>
            <Fld k="country" label="Country" f={form} setF={setForm}/>
            <Fld k="website" label="Website" f={form} setF={setForm}/>
            <Fld k="phone" label="Phone" f={form} setF={setForm}/>

            {/* ---------- HIRING STATES ---------- */}
            <div className="md:col-span-2 border border-slate-200 rounded-lg p-4 bg-slate-50/60" data-testid="hiring-states-block">
              <div className="flex items-center gap-2 mb-1"><MapPin size={14} className="text-sky-600"/><label className="text-xs font-semibold uppercase tracking-widest text-slate-700">Where do you expect to hire? (Optional)</label></div>
              <p className="text-[11px] text-slate-500 mb-3">Pick the U.S. states where you plan to hire. Signup is never blocked — we'll flag which states are currently ACTIVE, in REVIEW, or temporarily BLOCKED so you know what to expect.</p>

              {pickedStates.length > 0 && (
                <div className="mb-3 flex flex-wrap gap-2" data-testid="picked-states-summary">
                  <span className="text-[10px] font-semibold text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-2 py-0.5">Active {summary.ACTIVE || 0}</span>
                  <span className="text-[10px] font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-0.5">Review {summary.REVIEW || 0}</span>
                  <span className="text-[10px] font-semibold text-red-700 bg-red-50 border border-red-200 rounded px-2 py-0.5">Blocked {summary.BLOCKED || 0}</span>
                  <span className="text-[10px] text-slate-500">{pickedStates.length} selected</span>
                </div>
              )}

              <input value={stateFilter} onChange={e=>setStateFilter(e.target.value)}
                     placeholder="Search state or code…" data-testid="state-search"
                     className="w-full h-9 px-3 rounded-md border border-slate-300 text-sm mb-3 bg-white"/>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-1 max-h-48 overflow-y-auto pr-1">
                {filteredStates.map(s => {
                  const on = pickedStates.includes(s.state);
                  const Icon = STATUS_ICON[s.status];
                  return (
                    <button type="button" key={s.state_code} onClick={()=>toggleState(s)}
                            data-testid={`state-opt-${s.state_code}`}
                            className={`flex items-center justify-between gap-2 px-2.5 py-1.5 rounded border text-xs ${on ? "bg-white border-sky-500 ring-1 ring-sky-500" : "bg-white border-slate-200 hover:border-slate-400"}`}>
                      <span className="truncate text-slate-800">{on ? "✓ " : ""}{s.state}</span>
                      <span className={`inline-flex items-center gap-0.5 text-[9px] font-semibold border rounded px-1 py-0.5 ${STATUS_TONE[s.status]}`}><Icon size={9}/> {s.status}</span>
                    </button>
                  );
                })}
              </div>

              {(summary.BLOCKED || summary.REVIEW) ? (
                <div className="mt-3 text-[11px] text-slate-600 bg-white border border-slate-200 rounded p-2.5" data-testid="unavailable-note">
                  You've selected states that aren't currently available for ConnectMyHub Direct Hire. You can still create your account and hire in your ACTIVE states — we'll let you know as we expand availability.
                </div>
              ) : null}
            </div>

            <div className="md:col-span-2">
              <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5">How did you hear about ConnectMyHub?</label>
              <select value={form.sales_source} onChange={e=>setForm({...form, sales_source:e.target.value})} data-testid="signup-source" className="w-full h-10 px-3 rounded-md border border-slate-300 text-sm">
                {SALES_SOURCES.map(([v,l])=><option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <button data-testid="employer-signup-btn" disabled={busy} className="md:col-span-2 h-11 rounded-md bg-slate-900 text-white text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50">{busy && <Loader2 size={14} className="animate-spin"/>} Create employer account</button>
            <Link to="/login" className="md:col-span-2 text-center text-xs text-slate-500 hover:text-slate-800">Already have an account? Sign in</Link>
          </form>
        ) : (
          <div className="space-y-4">
            <div className="p-4 rounded-lg bg-emerald-50 border border-emerald-200">
              <div className="flex items-center gap-2 mb-1"><CheckCircle2 size={16} className="text-emerald-600"/><span className="text-sm font-semibold text-emerald-900">Account created</span></div>
              <p className="text-xs text-emerald-800">{result.email_sent
                ? "We just emailed you a verification link. Click the link to verify your email, then a ConnectMyHub admin will review your account."
                : "Email delivery is not yet configured. Use the verification token below to complete verification manually."}</p>
            </div>

            {(result.hiring_states_eligibility || []).length > 0 && (
              <div className="p-4 rounded-lg bg-white border border-slate-200" data-testid="signup-eligibility-summary">
                <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-2">Your hiring-state eligibility</div>
                <div className="space-y-1.5">
                  {result.hiring_states_eligibility.map(v => (
                    <div key={v.state_code || v.state} className="flex items-center justify-between text-xs">
                      <span className="text-slate-800">{v.state}</span>
                      <span className={`inline-flex items-center gap-1 text-[10px] font-semibold border rounded px-2 py-0.5 ${STATUS_TONE[v.status] || "text-slate-600 bg-slate-50 border-slate-200"}`}>{v.status}</span>
                    </div>
                  ))}
                </div>
                <div className="text-[10px] text-slate-500 mt-2">Only ACTIVE states can accept Direct Hire transactions today. You can still use ConnectMyHub for eligible jobs.</div>
              </div>
            )}

            {!result.email_sent && (<>
              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5">Verification token (dev fallback)</label>
                <input value={token} onChange={e=>setToken(e.target.value)} data-testid="verify-token" className="w-full h-10 px-3 rounded-md border border-slate-300 text-sm font-mono"/>
              </div>
              <button data-testid="verify-btn" onClick={verify} disabled={verifyBusy || !token} className="w-full h-11 rounded-md bg-sky-600 text-white text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50">{verifyBusy && <Loader2 size={14} className="animate-spin"/>} Verify email</button>
            </>)}
          </div>
        )}
      </div>
      <Toaster position="top-right" richColors/>
    </div>
  );
}
const Fld = ({k, label, type="text", f, setF, required, className=""}) => (
  <div className={className}>
    <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5">{label}{required?"*":""}</label>
    <input type={type} required={required} data-testid={`signup-${k}`} value={f[k]||""} onChange={e=>setF({...f,[k]:e.target.value})} className="w-full h-10 px-3 rounded-md border border-slate-300 text-sm focus:border-sky-500 outline-none"/>
  </div>
);
